/**
 * Answer guides: extra instructions sent to the agent for kinds of question whose
 * answers QA found too shallow. Each guide is matched on the user's question and
 * added after the SharePoint grounding rules, so it shapes the answer but never
 * loosens grounding: every guide still asks only for what the documents state.
 */

import {
  isTableSeparatorRow,
  normalizeMarkdownTables,
  splitTableCells,
} from "./copilotTables"

export type AnswerGuide = "recommendation" | "portfolio"

/**
 * LAB-104: "which product should we offer" questions. The agent tended to name one
 * product from one document, and two wordings of the same question could get two
 * different products, with no comparison or selection criteria for a sales call.
 */
const RECOMMENDATION_QUESTION =
  /\b(?:recommend\w*|suggest\w*|propose|which\s+(?:\w+\s+){0,3}(?:should|would|is\s+best|suits?|fits?)|best\s+(?:\w+\s+){0,2}for|most\s+suitable|suitable\s+for|right\s+(?:instrument|product|model)\s+for|what\s+should\s+we\s+(?:offer|quote|sell))\b/i

export const RECOMMENDATION_GUIDANCE = [
  "Recommendation request: write the answer for a sales or pre-sales conversation, using only what the SharePoint documents state.",
  "Start with the application's needs as the documents describe them (what is measured, ranges, sample type, standards).",
  "Recommend the best-fit model and say why it fits each need, naming the document for each reason.",
  "Give the selection criteria that decide between models (for example range, sensitivity, sample size, temperature control, throughput, compliance).",
  "Compare the recommended model with the strongest alternative model named in the documents, in a table with one column per model. Name a specific model, not only a type of instrument.",
  "Say when to choose the alternative instead.",
  "Use every relevant document (brochure, datasheet, application note), not only one, and name each in the claims it supports.",
  "End with what to confirm with the customer before quoting, only where the documents state it.",
]

/**
 * LAB-105: portfolio, positioning and "what's new" questions. Answers leaned on a
 * single brochure (missing the competitor comparison document), did not check
 * specifications that differ between documents, and blurred existing capabilities
 * with dated updates.
 */
const PORTFOLIO_QUESTION = new RegExp(
  [
    "\\bportfolio\\b",
    "\\bproduct\\s+(?:line|range|family|lineup)\\b",
    "\\bline-?up\\b",
    "\\bpositioning\\b",
    "\\bposition\\s+(?:\\w+\\s+){0,3}against\\b",
    "\\bcompetit\\w*",
    "\\bwhat(?:'s|\\s+is|\\s+was|\\s+were)\\s+(?:new|updated|changed)\\b",
    "\\b(?:latest|recent|new)\\s+(?:updates?|releases?|models?|versions?|features?)\\b",
    "\\b(?:updated?|updates|released?|launch(?:ed)?)\\s+in\\s+20\\d\\d\\b",
    "\\b20\\d\\d\\s+(?:updates?|releases?|launch(?:es)?|models?|versions?)\\b",
  ].join("|"),
  "i"
)

export const PORTFOLIO_GUIDANCE = [
  "Portfolio or positioning request: give a complete, verified answer from the SharePoint documents.",
  "Summarise the portfolio as a table: model, key specifications, typical applications, and the document for each row.",
  "Check each specification against the cited document. If documents give different values for the same specification, show both values with their documents and say they differ.",
  "Keep existing capabilities and new updates apart. Call something new, or an update for a given year, only when a cited document says so and gives the date or release. Otherwise list it as an existing capability, and give the document's date when it states one.",
  "If no document describes updates for the period asked, say that plainly instead of guessing.",
  "For positioning, use any comparison or competitor document that covers the product, compare only on points a document states, and do not add claims about other brands that the documents do not make.",
  "Use every relevant document, not only one, and name the document in each claim.",
]

const GUIDES: { guide: AnswerGuide; question: RegExp; guidance: string[] }[] = [
  {
    guide: "recommendation",
    question: RECOMMENDATION_QUESTION,
    guidance: RECOMMENDATION_GUIDANCE,
  },
  {
    guide: "portfolio",
    question: PORTFOLIO_QUESTION,
    guidance: PORTFOLIO_GUIDANCE,
  },
]

export function answerGuidesFor(question: string): AnswerGuide[] {
  return GUIDES.filter(({ question: pattern }) =>
    pattern.test(question || "")
  ).map(({ guide }) => guide)
}

/** The instruction lines to add for this question; empty when no guide applies. */
export function answerGuidance(question: string): string[] {
  return GUIDES.filter(({ question: pattern }) =>
    pattern.test(question || "")
  ).flatMap(({ guidance }) => guidance)
}

// ---------------------------------------------------------------------------
// LAB-104 follow-up: the agent does not always add the comparison the guide asks
// for (QA retest, 29 Oct: two of two answers had none), so the chat checks the
// answer and, when it is missing, asks once in the same conversation.

/** Column headers that describe one product rather than name another one. */
const GENERIC_COLUMN =
  /\b(?:documents?|sources?|references?|values?|notes?|why|reasons?|fits?|evidence|support\w*|details?|descriptions?|comments?|requirements?|criteri\w*|specs?|specifications?|statements?|states|figures?|use|uses|applications?)\b/i
const PRODUCT_COLUMN =
  /\b(?:products?|models?|instruments?|options?|alternatives?)\b/i

/** No alternative to compare: an answer saying so needs no follow-up. */
const NO_ALTERNATIVE =
  /\b(?:no\s+(?:other\s+|alternative\s+|second\s+)?(?:models?|instruments?|products?|alternatives?)\s+(?:\w+\s+){0,4}(?:named|documented|described|listed|mentioned|available|found)|(?:documents?|sources?)\s+(?:do|does)\s+not\s+(?:name|mention|describe|include|list)\s+(?:an?|any)\s+(?:other\s+|alternative\s+)?(?:models?|instruments?|products?|alternatives?))\b/i

function markdownTables(answer: string): string[][][] {
  const tables: string[][][] = []
  let rows: string[][] = []
  normalizeMarkdownTables(answer || "")
    .split("\n")
    .concat("")
    .forEach((raw) => {
      const line = raw.trim()
      if (line.startsWith("|") && line.endsWith("|")) {
        if (!isTableSeparatorRow(line)) rows.push(splitTableCells(line))
        return
      }
      if (rows.length >= 2) tables.push(rows)
      rows = []
    })
  return tables
}

/**
 * A table that compares two or more models: either two or more columns named
 * after products ("Criterion | DVNext | KU-3"), or rows that are products
 * ("Product | Range | Use" with two or more product rows). A table of one
 * product's fit ("Requirement | How the DCR fits | Document") does not count.
 */
export function hasModelComparisonTable(answer: string) {
  return markdownTables(answer).some(([header, ...body]) => {
    const productColumns = header
      .slice(1)
      .filter((cell) => cell && !GENERIC_COLUMN.test(cell))
    if (productColumns.length >= 2) return true
    return PRODUCT_COLUMN.test(header[0] || "") && body.length >= 2
  })
}

export function statesNoAlternative(answer: string) {
  return NO_ALTERNATIVE.test(answer || "")
}

/** True when a recommendation answer still lacks the model-vs-model comparison. */
export function needsComparisonFollowUp(question: string, answer: string) {
  if (!answerGuidesFor(question).includes("recommendation")) return false
  if (!(answer || "").trim()) return false
  return !hasModelComparisonTable(answer) && !statesNoAlternative(answer)
}

export const COMPARISON_FOLLOW_UP = [
  "Continue your recommendation above, using only what the SharePoint documents state.",
  "Add a table that compares the recommended model with the strongest alternative model named in the documents: one column per model, one row per selection criterion, and the supporting document for each value.",
  "Name a specific model, not only a type of instrument.",
  "Then say in one or two sentences when to choose the alternative instead.",
  "If the documents name no alternative model, say that in one sentence instead of adding a table.",
].join(" ")
